'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { type ComponentType, useState } from 'react';
import { z } from 'zod';

import { AiChatInput } from '@/components/AiChatInput/AiChatInput';
import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import { VStack } from '@/components/Layout';
import RootHeader from '@/components/Layout/RootHeader';
import {
  ProductLogoSmallAttendanceCore,
  ProductLogoSmallContractorCore,
  ProductLogoSmallITOperationCore,
  ProductLogoSmallLaborCore,
} from '@/components/Logo';
import { Text } from '@/components/Typography';
import { Separator } from '@/components/ui/separator';
import { useDesktopOrMobileMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/cn';
import { CockpitMockSidebar } from '@/routes/_cockpit-mock/cockpit-mock/-components/CockpitMockSidebar';
import { CockpitTaskCard } from '@/routes/_cockpit-mock/cockpit-mock/-components/CockpitTaskCard';
import { useCockpitTaskList } from '@/routes/_cockpit-mock/cockpit-mock/-query';
import GlobalHeaderPc from '@/routes/_individual/individual/-components/GlobalHeader/GlobalHeaderPc';
import GlobalHeaderSp from '@/routes/_individual/individual/-components/GlobalHeader/GlobalHeaderSp';
import { IndividualSidebar } from '@/routes/_individual/individual/-components/IndividualSidebar';

import { loadChatLandingData } from './-api';

type QuickCategory = {
  label: string;
  LogoComponent: ComponentType<{ size?: number; color?: string }>;
};

const QUICK_CATEGORIES: QuickCategory[] = [
  { label: '勤怠', LogoComponent: ProductLogoSmallAttendanceCore },
  { label: 'IT', LogoComponent: ProductLogoSmallITOperationCore },
  { label: '労務', LogoComponent: ProductLogoSmallLaborCore },
  { label: '業務契約', LogoComponent: ProductLogoSmallContractorCore },
];

const SearchSchema = z.object({
  from: z.enum(['individual', 'cockpit'] as const).optional(),
});

export const Route = createFileRoute('/_chat/chat/new/')({
  validateSearch: zodValidator(SearchSchema),
  loaderDeps: ({ search }) => ({ from: search.from ?? 'individual' }),
  loader: ({ deps }) => loadChatLandingData(deps.from),
  component: ChatWelcomePage,
});

function ChatWelcomePage() {
  const { recentSessions, upcomingInteractions } = Route.useLoaderData();
  const { from = 'individual' } = Route.useSearch();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isCockpit = from === 'cockpit';
  const cockpitTasks = useCockpitTaskList();
  const tasks = isCockpit ? (cockpitTasks.data ?? []) : [];
  const { isDesktop } = useDesktopOrMobileMediaQuery();

  return (
    <VStack className={cn('h-full', isCockpit ? '' : 'bg-blue-50')}>
      {isCockpit ? (
        <RootHeader />
      ) : isDesktop ? (
        <GlobalHeaderPc />
      ) : (
        <GlobalHeaderSp />
      )}
      <Separator />
      <div className="flex min-h-0 w-full flex-1 overflow-hidden bg-neutral-bottom-fill">
        {isCockpit ? (
          <CockpitMockSidebar
            mode="chat"
            open={sidebarOpen}
            onToggle={() => setSidebarOpen((v) => !v)}
            recentSessions={recentSessions}
            upcomingInteractions={upcomingInteractions}
          />
        ) : (
          <IndividualSidebar
            mode="chat"
            open={sidebarOpen}
            onToggle={() => setSidebarOpen((v) => !v)}
            recentSessions={recentSessions}
            upcomingInteractions={upcomingInteractions}
          />
        )}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto px-6 py-10">
          <div className="mx-auto my-auto flex w-full max-w-[720px] flex-col items-center gap-8">
            <div className="flex items-center gap-3">
              <span
                className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-muted-fill"
                aria-hidden="true"
              >
                <Icon
                  iconName="Sparkles"
                  size={24}
                  className="text-primary-on-muted-fill"
                  inheritColor
                />
              </span>
              <Text
                as="h2"
                variant="h2"
                className="capitalize text-neutral-on-fill"
              >
                {isCockpit
                  ? 'お疲れさまです、田中さん'
                  : 'こんにちは、田中さん'}
              </Text>
            </div>

            <div className="w-full">
              <AiChatInput className="max-w-none" />
            </div>

            {isCockpit ? (
              <div className="flex w-full flex-col gap-3">
                <Text
                  variant="label"
                  className="px-1 text-neutral-muted-on-fill"
                >
                  対応待ちのタスク
                </Text>
                <ul className="flex flex-col gap-2">
                  {tasks.map((task) => (
                    <li key={task.id}>
                      <CockpitTaskCard item={task} />
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <ul className="flex flex-wrap items-center justify-center gap-5">
                {QUICK_CATEGORIES.map(({ label, LogoComponent }) => (
                  <li key={label}>
                    <ActionButton
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-neutral-muted-on-fill"
                    >
                      <LogoComponent
                        size={16}
                        color="var(--color-neutral-muted-on-fill)"
                      />
                      {label}
                    </ActionButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </main>
      </div>
    </VStack>
  );
}
